import React, { useEffect, useState } from "react";
import {
  Box,
  Modal,
  Typography,
  IconButton,
  Button,
  MenuItem,
  TextField,
  FormControl,
  CircularProgress,
} from "@mui/material";
import { Cancel } from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { CustomerDatabaseStore } from "../../../../../store/main/CustomerDatabaseStore";
import { MonthlyTourProgramStore } from "../../../../../store/main/MonthlyTourProgramStore";
import moment from "moment";
import { FetchUserFieldWorkChildrenType } from "../../../../../domain/models/monthly-tour-program/fetch-user-field-work-children";
import Swal from "sweetalert2";

interface AddTourProgramModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  tourTypes: string[];
  defaultDate?: string; // New prop for default date
  meetingDates: string[];
  existingTours?: { date: string; type: string }[]; // list of existing tours with date and type
}

interface FormValues {
  date: string;
  typeOfTour: string;
  workingWith?: number;
  territory?: number;
  brick?: number;
}

const modalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 220,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: "1rem 3rem", // Top-bottom 1, left-right 3
  borderRadius: 2,
  transition: "height 0.3s ease",
};

const dropdownStyle = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#226E9E", // Dropdown border color
    },
    "&:hover fieldset": {
      borderColor: "#226E9E",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#226E9E",
    },
  },
};

interface PayloadType {
  date: string;
  hq_brick_id: string;
  hq_territory_id: string;
  type: string;
  user_id: number;
  working_with_id?: string; // optional
}

const AddTourProgramFormModal: React.FC<AddTourProgramModalProps> = ({
  open,
  onClose,
  onSave,
  tourTypes,
  defaultDate,
  meetingDates,
  existingTours,
}) => {
  const { decodeToken, userRole, userDetails } = CustomerDatabaseStore();
  const {
    fetchUserFieldWorkChildren,
    fetchUserTerritory,
    fetchUserTerritoryBricks,
    addTourProgramForm,
    fetchHeadQuarterListTour,
  } = MonthlyTourProgramStore();

  const [workingWithOptions, setWorkingWithOptions] = useState<
    { id: number; name: string; headquarter_id: number }[]
  >([]);
  const [territoryOptions, setTerritoryOptions] = useState<
    { id: number; name: string }[]
  >([]);
  const [territoriesBricksOptions, setTerritoriesBricksOptions] = useState<
    { id: number; name: string }[]
  >([]);
  const [dropdownLoading, setDropdownLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDateBlocked, setIsDateBlocked] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [tourTypeError, setTourTypeError] = useState("");

  const { control, handleSubmit, watch, reset, setValue } = useForm<FormValues>(
    {
      defaultValues: {
        date: defaultDate || "",
        typeOfTour: "",
        workingWith: undefined,
        territory: undefined,
        brick: undefined,
      },
    }
  );

  const dateValue = watch("date");
  const typeOfTour = watch("typeOfTour");
  const workingWith = watch("workingWith");
  const territory = watch("territory");
  const brick = watch("brick");

  useEffect(() => {
    decodeToken();
  }, []);

  useEffect(() => {
    if (meetingDates && dateValue) {
      setIsDateBlocked(meetingDates.includes(dateValue));
    } else {
      setIsDateBlocked(false);
    }
  }, [dateValue, meetingDates]);

  useEffect(() => {
    if (open) {
      reset({
        date: defaultDate || "",
        typeOfTour: "",
        workingWith: undefined,
        territory: undefined,
        brick: undefined,
      });
    }
  }, [open, defaultDate, reset]);

  useEffect(() => {
    if (!open) {
      setErrorMessage("");
      setTourTypeError("");
    }
  }, [open]);

  useEffect(() => {
    if (
      typeOfTour === "Field Work" &&
      userDetails?.role_id &&
      userDetails?.id
    ) {
      const payload: { role_id: string; status: string; manager_id?: number } =
        {
          role_id: "3",
          status: "active",
        };

      // Only include manager_id if role_id < 5
      if (Number(userDetails.role_id) < 5) {
        payload.manager_id = userDetails.id;
      }
      // Explicitly do not include manager_id if role_id >= 5
      // No need to delete, as it's optional
      setDropdownLoading(true);
      fetchUserFieldWorkChildren(payload)
        .then((data: FetchUserFieldWorkChildrenType) => {
          const users = Array.isArray(data?.users) ? data.users : [];
          const mappedData = users.map((user: any) => ({
            id: user.id ?? 0,
            name: user.full_name || "Unknown User",
            headquarter_id: user.hq_headquarter.id || null,
          }));
          const finalOptions = [
            { id: 0, name: "Working Alone", headquarter_id: 0 },
            ...mappedData,
          ];
          setWorkingWithOptions(finalOptions);
        })
        .catch(() => {
          setWorkingWithOptions([]);
        })
        .finally(() => {
          setDropdownLoading(false);
        });
    } else if (typeOfTour !== "Field Work") {
      setWorkingWithOptions([]);
    }
  }, [
    typeOfTour,
    fetchUserFieldWorkChildren,
    userDetails,
    fetchHeadQuarterListTour,
  ]);

  // Fetch Territories whenever typeOfTour is Field Work (not based on workingWith)
  useEffect(() => {
    if (typeOfTour === "Field Work") {
      setDropdownLoading(true);
      fetchHeadQuarterListTour()
        .then((data: any) => {
          const territories = Array.isArray(data?.headquarters)
            ? data.headquarters
            : [];

          const mappedData = territories.map((t: any) => ({
            id: t.id ?? 0,
            name: t.name || "Unknown Territory",
          }));

          setTerritoryOptions(mappedData);
        })
        .catch(() => {
          setTerritoryOptions([]);
        })
        .finally(() => {
          setDropdownLoading(false);
        });
    } else {
      setTerritoryOptions([]);
    }
  }, [typeOfTour, fetchHeadQuarterListTour]);

  useEffect(() => {
    if (territory && territoryOptions.length > 0) {
      const payload = { headquarter_id: territory };
      setDropdownLoading(true);
      fetchUserTerritoryBricks(payload)
        .then((data: any) => {
          const territoriesBricks = Array.isArray(data?.bricks)
            ? data.bricks
            : [];

          const mappedData = territoriesBricks.map((t: any) => ({
            id: t.id ?? 0,
            name: t.name || "Unknown Territory",
          }));

          setTerritoriesBricksOptions(mappedData);
        })
        .catch(() => {
          setTerritoriesBricksOptions([]);
        })
        .finally(() => {
          setDropdownLoading(false);
        });
    } else {
      setTerritoriesBricksOptions([]);
    }
  }, [territory, fetchUserTerritoryBricks]);

  useEffect(() => {
    if (typeOfTour !== "Field Work") {
      setWorkingWithOptions([]);
      setTerritoryOptions([]);
      setTerritoriesBricksOptions([]);
      reset({ typeOfTour, date: watch("date") });
    } else {
      reset({ typeOfTour, date: watch("date") });
    }
  }, [typeOfTour]);

  useEffect(() => {
    if (typeOfTour && existingTours && existingTours.length > 0) {
      // check if there are non-field-work tours already for this date
      const hasOtherTour = existingTours.some((t) => t.type !== "Field Work");

      if (hasOtherTour && typeOfTour !== "Field Work") {
        setTourTypeError("Only Field Work is allowed for this date.");
      } else {
        setTourTypeError("");
      }
    } else {
      setTourTypeError("");
    }
  }, [typeOfTour, existingTours]);

  useEffect(() => {
    setValue("brick", undefined);
    setTerritoriesBricksOptions([]);
  }, [territory]);

  const showWorkingWith = typeOfTour === "Field Work";
  const showTerritory = showWorkingWith && workingWith !== undefined;
  const showBrick = showTerritory && territory !== undefined;
  const showSaveButton =
    typeOfTour !== "" && (typeOfTour !== "Field Work" || (showBrick && brick));

  useEffect(() => {
    if (typeOfTour === "Field Work" && isDateBlocked) {
      setErrorMessage(
        "You cannot add Field Work for a date that already has a Meeting."
      );
    } else {
      setErrorMessage("");
    }
  }, [typeOfTour, isDateBlocked]);

  const onSubmit = async (formValues: FormValues) => {
    if (isSubmitting) return; // prevent double-click

    const payload = {
      tours: [
        {
          date: formValues.date,
          hq_brick_id: formValues.brick ? String(formValues.brick) : "",
          type: formValues.typeOfTour,
          user_id: userDetails?.id || 0,
          working_with_id:
            userDetails?.role?.name !== "HQ_MNG" &&
            formValues.workingWith &&
            formValues.workingWith !== 0
              ? String(formValues.workingWith)
              : "",
        },
      ],
    };
    setIsSubmitting(true);
    try {
      await addTourProgramForm(payload);
      onSave(payload);
      onClose();

      // ✅ SweetAlert success popup
      Swal.fire({
        icon: "success",
        title: "Tour Program Added",
        text: "Your tour program has been successfully created!",
        confirmButtonColor: "#3085d6",
      });
    } catch (err: any) {
      console.error("Error submitting tour program:", err);

      // ✅ SweetAlert error popup
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text:
          err?.response?.data?.tours?.[0] ||
          "Something went wrong while submitting the tour program.",
        confirmButtonColor: "#d33",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const minDate = moment().startOf("month").format("YYYY-MM-DD");
  const maxDate = moment().add(1, "month").endOf("month").format("YYYY-MM-DD");

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        {/* Heading */}
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          mb={1}
          position="relative"
        >
          <Typography
            variant="subtitle2"
            align="center"
            sx={{ color: "#226E9E", fontWeight: 600 }}
          >
            Add Tour Program
          </Typography>
          {/* Cancel button more to the right */}
          <IconButton
            size="small"
            sx={{ position: "absolute", right: "-32px", top: "-4px" }} // adjust values to push further right/top
            onClick={() => {
              reset();
              setErrorMessage("");
              setTourTypeError(""); // ✅ clear custom error
              onClose();
            }}
          >
            <Cancel fontSize="small" sx={{ color: "#226E9E" }} />
          </IconButton>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Date Picker */}
          <Controller
            name="date"
            control={control}
            rules={{ required: "Date is required" }}
            render={({ field, fieldState: { error } }) => (
              <>
                <TextField
                  {...field}
                  label="Date"
                  type="date"
                  fullWidth
                  margin="dense"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ readOnly: true, min: minDate, max: maxDate }}
                  size="small"
                  sx={dropdownStyle}
                  error={!!error}
                  helperText={error?.message}
                />
              </>
            )}
          />

          {/* Type of Tour */}
          <Controller
            name="typeOfTour"
            control={control}
            rules={{ required: "Type of Tour is required" }}
            render={({ field, fieldState: { error } }) => {
              const showHelperText =
                error?.message || errorMessage || tourTypeError;

              return (
                <FormControl fullWidth margin="dense">
                  <TextField
                    select
                    label="Type of Tour"
                    {...field}
                    size="small"
                    sx={dropdownStyle}
                    disabled={isDateBlocked}
                    error={Boolean(showHelperText)}
                    helperText={
                      <span style={{ color: "red", fontSize: "0.75rem" }}>
                        {showHelperText}
                      </span>
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                      setErrorMessage("");
                      setTourTypeError("");

                      // ✅ Detect if Field Work already exists for that date
                      const hasFieldWorkTour = existingTours?.some(
                        (t: any) =>
                          t.date === dateValue && t.type === "Field Work"
                      );

                      // ✅ Detect if other type already exists for that date
                      const hasOtherTour = existingTours?.some(
                        (t: any) =>
                          t.date === dateValue && t.type !== "Field Work"
                      );

                      // 🚫 Rule 1: Date has a meeting -> block Field Work
                      if (isDateBlocked && value === "Field Work") {
                        setErrorMessage(
                          "You cannot add Field Work for a date that already has a Meeting."
                        );
                        setValue("typeOfTour", "");
                        return;
                      }

                      // 🚫 Rule 2: Has Field Work already -> only allow more Field Work
                      if (hasFieldWorkTour && value !== "Field Work") {
                        setTourTypeError(
                          "Only 'Field Work' tours can be added when one already exists."
                        );
                        setValue("typeOfTour", "");
                        return;
                      }

                      // 🚫 Rule 3: Has any other type already -> no multiple tours
                      if (hasOtherTour) {
                        setTourTypeError(
                          "You cannot add multiple tours for this type. Only Field Work can have multiple tours."
                        );
                        setValue("typeOfTour", "");
                        return;
                      }
                    }}
                  >
                    {tourTypes.map((type) => (
                      <MenuItem
                        key={type}
                        value={type}
                        disabled={
                          isDateBlocked && type === "Field Work" // block field work on meeting day
                        }
                      >
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormControl>
              );
            }}
          />

          {/* Show Working With, Territory, Brick only when Field Work is selected */}
          {typeOfTour === "Field Work" && (
            <>
              {/* Territory (Required & Always Enabled when Field Work is selected) */}
              <Controller
                name="territory"
                control={control}
                rules={{
                  required: "Territory is required for Field Work",
                }}
                render={({ field, fieldState }) => (
                  <FormControl fullWidth margin="dense">
                    <TextField
                      select
                      label="Territory"
                      {...field}
                      size="small"
                      sx={dropdownStyle}
                      disabled={
                        dropdownLoading || territoryOptions.length === 0
                      }
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      onChange={(e) => {
                        field.onChange(e);
                        // Reset brick when territory changes
                        setValue("brick", undefined);
                      }}
                      InputProps={{
                        endAdornment: dropdownLoading ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              pr: 1,
                            }}
                          >
                            <CircularProgress size={20} color="inherit" />
                          </Box>
                        ) : null,
                      }}
                    >
                      {territoryOptions.length > 0
                        ? territoryOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.name}
                            </MenuItem>
                          ))
                        : !dropdownLoading && (
                            <MenuItem disabled>No options</MenuItem>
                          )}
                    </TextField>
                  </FormControl>
                )}
              />

              {/* Working With (Optional) */}
              {userDetails.role.name !== "HQ_MNG" && (
                <Controller
                  name="workingWith"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth margin="dense">
                      <TextField
                        select
                        label="Working With"
                        {...field}
                        size="small"
                        sx={dropdownStyle}
                        disabled={dropdownLoading}
                        InputProps={{
                          endAdornment: dropdownLoading ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                pr: 1,
                              }}
                            >
                              <CircularProgress size={20} color="inherit" />
                            </Box>
                          ) : null,
                        }}
                      >
                        {workingWithOptions.length > 0
                          ? workingWithOptions.map((option) => (
                              <MenuItem key={option.id} value={option.id}>
                                {option.name}
                              </MenuItem>
                            ))
                          : !dropdownLoading && (
                              <MenuItem disabled>No options</MenuItem>
                            )}
                      </TextField>
                    </FormControl>
                  )}
                />
              )}

              {/* Brick (Required & Enabled only after Territory selection) */}
              <Controller
                name="brick"
                control={control}
                rules={{
                  required: "Brick is required for Field Work",
                }}
                render={({ field, fieldState }) => {
                  const territorySelected = !!watch("territory");
                  return (
                    <FormControl fullWidth margin="dense">
                      <TextField
                        select
                        label="Brick"
                        {...field}
                        size="small"
                        sx={dropdownStyle}
                        disabled={
                          dropdownLoading ||
                          territoriesBricksOptions.length === 0 ||
                          !territorySelected
                        }
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        InputProps={{
                          endAdornment: dropdownLoading ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                pr: 1,
                              }}
                            >
                              <CircularProgress size={20} color="inherit" />
                            </Box>
                          ) : null,
                        }}
                      >
                        {territoriesBricksOptions.length > 0
                          ? territoriesBricksOptions.map((option) => (
                              <MenuItem key={option.id} value={option.id}>
                                {option.name}
                              </MenuItem>
                            ))
                          : !dropdownLoading && (
                              <MenuItem disabled>No options</MenuItem>
                            )}
                      </TextField>
                    </FormControl>
                  );
                }}
              />
            </>
          )}

          {/* Save Button */}
          <Box display="flex" justifyContent="center" mt={2}>
            <Button
              variant="contained"
              type="submit"
              size="small"
              fullWidth
              sx={{ maxWidth: "100%" }}
              disabled={isSubmitting || isDateBlocked} // ✅ disable submit
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

export default AddTourProgramFormModal;
